import { useEffect, useState, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { Globe2, Eye, MousePointerClick, Inbox, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const ranges = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

export default function Analytics() {
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/analytics?range=${range}`).then(({ data }) => {
      setData(data);
      setRefreshedAt(new Date());
    }).catch(() => {});
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {ranges.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${range === r.key ? "bg-[#00BFFF] text-[#06122B]" : "bg-[#0E1642] text-slate-300 border border-white/10 hover:border-cyan-400/40"}`}
              data-testid={`analytics-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <button onClick={load} className="flex items-center gap-1.5 hover:text-cyan-300 transition-colors" data-testid="analytics-refresh-button">
            <RefreshCw size={13} /> Refresh
          </button>
          <span>· Auto-refreshes every 5 min{refreshedAt ? ` · Last refreshed ${refreshedAt.toLocaleTimeString()}` : ""}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Eye, label: "Total Visits", value: data?.total_visits },
          { icon: Globe2, label: "Unique Visitors", value: data?.unique_sessions },
          { icon: MousePointerClick, label: "Click Events", value: data?.total_events },
          { icon: Inbox, label: "New Leads", value: data?.leads },
        ].map((k) => (
          <div key={k.label} className="bg-[#0E1642] border border-white/10 rounded-2xl p-5">
            <k.icon size={18} className="text-cyan-400" />
            <div className="mt-3 font-display font-extrabold text-3xl text-white" data-testid="analytics-kpi-value">{k.value ?? "—"}</div>
            <div className="text-xs text-slate-400 mt-1 font-semibold">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
        <h3 className="font-display font-bold text-white mb-4">Visitors over time</h3>
        <div className="h-64" data-testid="analytics-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.series || []}>
              <defs>
                <linearGradient id="visitsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00BFFF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00BFFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0F2C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="visits" stroke="#00BFFF" strokeWidth={2.5} fill="url(#visitsGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
          <h3 className="font-display font-bold text-white mb-4">Country breakdown</h3>
          <table className="w-full text-sm" data-testid="analytics-country-table">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wide">
                <th className="pb-3 font-semibold">Country</th>
                <th className="pb-3 font-semibold text-right">Visits</th>
              </tr>
            </thead>
            <tbody>
              {(data?.countries || []).map((c) => (
                <tr key={c.country} className="border-t border-white/5">
                  <td className="py-2.5 text-slate-200">{c.country}</td>
                  <td className="py-2.5 text-right font-semibold text-cyan-300">{c.visits}</td>
                </tr>
              ))}
              {(!data?.countries || data.countries.length === 0) && (
                <tr><td colSpan={2} className="py-6 text-center text-slate-500">No visits in this period yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
          <h3 className="font-display font-bold text-white mb-4">Top click events</h3>
          <div className="space-y-2.5" data-testid="analytics-events-list">
            {(data?.events || []).slice(0, 8).map((e) => (
              <div key={e.name} className="flex items-center justify-between text-sm">
                <span className="text-slate-200 font-mono-brand text-xs">{e.name}</span>
                <span className="font-semibold text-cyan-300">{e.count}</span>
              </div>
            ))}
            {(!data?.events || data.events.length === 0) && (
              <div className="py-6 text-center text-slate-500 text-sm">No click events yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
